import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Collapse, Flex, Segmented, Space, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { INVENTORIZE_PHOTO_REVIEW_FIELD_IDS } from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import type {
  InventorizeReviewField,
  InventorizeReviewStatus,
} from '@/features/inventorization/domain/review/inventorize-review-fields';
import {
  countBlockingInventorizeReviewFields,
  getNextInventorizeReviewIssue,
  INVENTORIZE_REVIEW_STATUSES,
  summarizeInventorizeReviewFields,
} from '@/features/inventorization/domain/review/inventorize-review-fields';
import { useExistingMeterPhotos } from '@/features/inventorization/photos/useReviewPhotos';
import { reviewSectionLabel, reviewText } from '@/features/inventorization/review-i18n';
import { usePersistentReviewUiState } from '@/features/inventorization/usePersistentReviewUiState';
import { useLocationBootstrap } from '@/features/location-bootstrap/LocationBootstrapContext';
import { PhotoReviewGallery } from '@/routers/inventorization/components/review/PhotoReviewGallery';
import { RegisterReviewGroups } from '@/routers/inventorization/components/review/RegisterReviewGroups';
import { ReviewExtractionAlerts } from '@/routers/inventorization/components/review/ReviewExtractionAlerts';
import { ReviewFieldList } from '@/routers/inventorization/components/review/ReviewFieldList';
import { ReviewSummaryCard } from '@/routers/inventorization/components/review/ReviewSummaryCard';
import { type CaptureSession, PHOTO_CONTEXTS, type ReadinessSummary } from '@/shared/capture/capture-session';

interface InventorizeParsedReviewProps {
  extractionError: string | null;
  fields: InventorizeReviewField[];
  isQueueing: boolean;
  isExtractionPolling: boolean;
  onOpenField: (field: InventorizeReviewField) => void;
  onAddRegister: () => Promise<void> | void;
  onApproveField?: (field: InventorizeReviewField) => void;
  onCaptureRegisterPhoto: (registerId: string, file: File) => Promise<void> | void;
  onRemoveRegister: (registerId: string) => Promise<void> | void;
  onQueueOffline: () => Promise<void> | void;
  readiness: ReadinessSummary;
  session: CaptureSession;
}

const sectionOrder = [
  'Photos',
  'Location',
  'Identity',
  'Registers',
  'Meter details',
  'Remote reading',
  'Note',
] as const;

const REVIEW_FIELD_FILTERS = {
  all: 'all',
  empty: 'empty',
  required: 'required',
} as const;
type ReviewFieldFilter = (typeof REVIEW_FIELD_FILTERS)[keyof typeof REVIEW_FIELD_FILTERS];

export function InventorizeParsedReview({
  extractionError,
  fields,
  isQueueing,
  isExtractionPolling,
  onOpenField,
  onAddRegister,
  onApproveField,
  onCaptureRegisterPhoto,
  onRemoveRegister,
  onQueueOffline,
  readiness,
  session,
}: InventorizeParsedReviewProps) {
  const summary = summarizeInventorizeReviewFields(fields);
  const parsedFieldCount = summary.parsed + summary.unsure;
  const nextIssue = getNextInventorizeReviewIssue(fields);
  const blockingFieldCount = countBlockingInventorizeReviewFields(fields);
  const reviewUiState = usePersistentReviewUiState(session.id, sectionOrder);
  const remotePhotos = useExistingMeterPhotos(session);
  const { bootstrap } = useLocationBootstrap();
  const existingInternalNote = readExistingInternalNote(session, bootstrap?.existingMeters);
  const [fieldFilter, setFieldFilter] = useState<ReviewFieldFilter>(REVIEW_FIELD_FILTERS.all);
  const filteredFields = useMemo(
    () => fields.filter(field => matchesReviewFieldFilter(field, fieldFilter)),
    [fieldFilter, fields],
  );
  const handleOpenField = (field: InventorizeReviewField) => {
    reviewUiState.rememberScrollPosition(field.id);
    onOpenField(field);
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <ReviewSummaryCard
        emptyCount={blockingFieldCount}
        blockingFieldCount={blockingFieldCount}
        isQueueing={isQueueing}
        nextIssueExists={Boolean(nextIssue)}
        onFixNextIssue={() => {
          if (nextIssue) {
            handleOpenField(nextIssue);
          }
        }}
        onQueueOffline={onQueueOffline}
        parsedCount={parsedFieldCount}
        readiness={readiness}
        session={session}
      />

      <ReviewExtractionAlerts error={extractionError} isPolling={isExtractionPolling} session={session} />

      <Segmented<ReviewFieldFilter>
        block
        onChange={setFieldFilter}
        options={[
          { label: reviewText('filters.all', 'All fields'), value: REVIEW_FIELD_FILTERS.all },
          { label: reviewText('filters.required', 'Required'), value: REVIEW_FIELD_FILTERS.required },
          { label: reviewText('filters.empty', 'Empty'), value: REVIEW_FIELD_FILTERS.empty },
        ]}
        value={fieldFilter}
      />

      <Collapse
        activeKey={reviewUiState.activeSectionKeys}
        onChange={reviewUiState.setActiveSectionKeys}
        items={sectionOrder.flatMap(section => {
          const sectionFields = filteredFields.filter(field => field.section === section);

          if (sectionFields.length === 0) {
            return [];
          }

          const newPhotos = session.photos.filter(photo => !photo.uploadedRemoteId);
          const modulePhotos = newPhotos.filter(photo => photo.context === PHOTO_CONTEXTS.module);
          const shouldShowModuleGallery =
            section === 'Remote reading' &&
            (modulePhotos.length > 0 ||
              sectionFields.some(field => field.id === INVENTORIZE_PHOTO_REVIEW_FIELD_IDS.module));

          return [
            {
              children: (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {section === 'Photos' ? (
                    <PhotoReviewGallery
                      isLoadingRemote={remotePhotos.isLoading}
                      photos={newPhotos.filter(
                        photo => photo.context !== PHOTO_CONTEXTS.register && photo.context !== PHOTO_CONTEXTS.module,
                      )}
                      remoteError={remotePhotos.error}
                      remotePhotos={remotePhotos.photos}
                    />
                  ) : null}
                  {shouldShowModuleGallery ? <PhotoReviewGallery photos={modulePhotos} /> : null}
                  {section === 'Registers' ? (
                    <RegisterReviewGroups
                      fields={sectionFields}
                      onAddRegister={onAddRegister}
                      onApproveField={onApproveField}
                      onCaptureRegisterPhoto={onCaptureRegisterPhoto}
                      onOpenField={handleOpenField}
                      onRemoveRegister={onRemoveRegister}
                      session={session}
                    />
                  ) : (
                    <ReviewFieldList
                      fields={sectionFields}
                      onApproveField={onApproveField}
                      onOpenField={handleOpenField}
                    />
                  )}
                  {section === 'Note' && existingInternalNote ? (
                    <ReadOnlyInternalNote note={existingInternalNote} />
                  ) : null}
                </Space>
              ),
              key: section,
              label: (
                <Flex align="center" justify="space-between">
                  <Typography.Text strong>{reviewSectionLabel(section)}</Typography.Text>
                  <SectionProgress fields={sectionFields} />
                </Flex>
              ),
            },
          ];
        })}
      />
    </Space>
  );
}

function ReadOnlyInternalNote({ note }: { note: string }) {
  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Typography.Text strong>{reviewText('note.existingTitle', 'Internal note')}</Typography.Text>
      <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }} type="secondary">
        {note}
      </Typography.Paragraph>
    </Space>
  );
}

function readExistingInternalNote(
  session: CaptureSession,
  meters: Array<{ _id?: string; id?: number; internalNote?: string | null }> = [],
) {
  const meter = meters.find(candidate => candidate._id === session._id || candidate.id === session.meterId);

  return meter?.internalNote?.trim() ?? '';
}

function matchesReviewFieldFilter(field: InventorizeReviewField, filter: ReviewFieldFilter) {
  if (filter === REVIEW_FIELD_FILTERS.required) {
    return field.required;
  }

  if (filter === REVIEW_FIELD_FILTERS.empty) {
    return field.status === INVENTORIZE_REVIEW_STATUSES.empty;
  }

  return true;
}

function SectionProgress({ fields }: { fields: InventorizeReviewField[] }) {
  const filled = fields.filter(field => isFilledStatus(field.status)).length;
  const requiredComplete = fields.filter(field => field.required).every(field => isFilledStatus(field.status));

  return (
    <Tag
      color={requiredComplete ? 'green' : 'orange'}
      icon={requiredComplete ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
    >
      {filled}/{fields.length}
    </Tag>
  );
}

function isFilledStatus(status: InventorizeReviewStatus) {
  return (
    status === INVENTORIZE_REVIEW_STATUSES.manual ||
    status === INVENTORIZE_REVIEW_STATUSES.parsed ||
    status === INVENTORIZE_REVIEW_STATUSES.accepted
  );
}
